import React, { useEffect, useRef, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Icon, AuthBackdrop } from 'components/UI';
import { AuthErrorMessage, TwoFactorChallenge } from 'components/auth';

import { ROUTES } from 'routes';
import { emailRex } from 'settings';
import type { AuthFactorType } from '@repo/types';

import {
  Typography,
  Stack,
  Button,
  TextField,
  Divider,
  Box,
  InputAdornment,
  IconButton,
  useTheme,
} from '@mui/material';
import { Input } from 'components/UI/Input';
import { PasswordStrengthMeter } from 'components/UI/PasswordStrengthMeter';
import { SocialLoginButtons } from 'components/SocialLoginButtons/SocialLoginButtons';
import {
  useSendMagicAuthCode,
  useVerifyMagicAuthCode,
  useLoginWithPassword,
  useSetupPassword,
} from 'hooks/api/useWorkOSAuth';
import { useGetCompanyByEmbedKey, useGetCompanyBySubdomain } from 'hooks/api';
import { extractSubdomain, getEmbeddedKey, isVeraOwnedSurface, otpSendErrorMessage, otpVerifyErrorMessage } from 'utils';
import { CodeExpiryNotice } from 'components/auth';
import { OTP_CODE_TTL_SECONDS, useCodeExpiry } from 'hooks/useCodeExpiry';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { useAuth } from 'hooks';
import { CompanyContext } from 'contexts/companyContext';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@repo/shadcn-ui';
import { markPendingPostAuthStep, clearPendingPostAuthStep } from 'utils/postAuthStep';

export const SignInBanner = () => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUrl = window.location.href;
  const url = new URL(currentUrl);

  // VERA-838: Shopify auto-login hardening. When App.tsx's silent
  // auto-login attempt fails and falls back to this manual sign-in surface,
  // it hands off a clear reason via router state instead of dropping the
  // user here with no explanation. Read once on mount; the per-step
  // `error` state below (email/code/password validation) is separate so
  // this banner doesn't get clobbered or reused for unrelated form errors.
  const [authError] = useState<string | null>(
    (location.state as { authError?: string } | null)?.authError ?? null
  );

  // Magic Auth state
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  // VRG-720 — the outstanding code's send time, so its 10-minute deadline is
  // visible and an expiry is distinguishable from a mistyped code.
  const [codeIssuedAt, setCodeIssuedAt] = useState<number | null>(null);
  const { expired: codeExpired } = useCodeExpiry(codeIssuedAt, OTP_CODE_TTL_SECONDS);
  const [error, setError] = useState<string | null>(null);

  // Password sign-in state
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordLoggingIn, setIsPasswordLoggingIn] = useState(false);
  const passwordLoginInFlightRef = useRef(false);

  // Post-OTP "set a password" step (non-blocking, offered to new sign-ins)
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const setPasswordInFlightRef = useRef(false);
  // Suppresses the isAuthenticated auto-navigate effect below while we're
  // showing the post-verify "set a password" screen (the verify mutation's
  // onSuccess flips isAuthenticated=true before we've decided whether to
  // show that screen, so this ref-based flag is set synchronously before
  // the mutation fires to avoid a race with the effect).
  const suppressAutoNavRef = useRef(false);

  const [isSendingCode, setIsSendingCode] = useState(false);
  const sendCodeInFlightRef = useRef(false);

  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const verifyInFlightRef = useRef(false);

  // VRG-490 §2 — a verified second factor withholds the session; either
  // `verifyMagicAuthCode` or `loginWithPassword` below can return this
  // instead of a token. Owners have no mandatory-enrollment tier (D1 is
  // `platform_admin`-only), so there is no `enrollmentRequired` screen here.
  const [challenge, setChallenge] = useState<{ ticket: string; factorType: AuthFactorType } | null>(null);

  const sendMagicAuthCode = useSendMagicAuthCode();
  const verifyMagicAuthCode = useVerifyMagicAuthCode();
  const loginWithPassword = useLoginWithPassword();
  const setupPassword = useSetupPassword();

  const { companyId } = useContext(CompanyContext);

  const handleSendCode = async () => {
    if (sendCodeInFlightRef.current) return;

    // VERA-837: validate the FORMAT, not just presence. `!email` alone let
    // "sdfa" through to the API, which 400s, and the raw backend message was
    // rendered verbatim as "Bad Request" — an HTTP status shown as user copy.
    if (!email || !emailRex.test(email)) {
      setError('Enter a valid email address.');
      return;
    }

    sendCodeInFlightRef.current = true;
    setIsSendingCode(true);
    setError(null);
    try {
      await sendMagicAuthCode.mutateAsync({ email, companyId });
      setCodeSent(true);
      // Resend overwrites the stored code and restarts its TTL, so restart here.
      setCodeIssuedAt(Date.now());
    } catch (err: any) {
      // Never surface a bare HTTP status ("Bad Request", "Forbidden") as copy.
      setError(otpSendErrorMessage(err));
    } finally {
      sendCodeInFlightRef.current = false;
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verifyInFlightRef.current) return;

    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit code.');
      return;
    }

    verifyInFlightRef.current = true;
    setIsVerifyingCode(true);
    setError(null);
    suppressAutoNavRef.current = true;
    // VERA-842: must be set BEFORE the mutation. `useVerifyMagicAuthCode`'s
    // onSuccess dispatches SET_AUTH while we're still awaiting, so
    // ProtectedAuthRoute re-renders (authenticated) mid-await — setting this
    // afterwards is too late, the page is already unmounted. Cleared below in
    // every path that doesn't actually show a post-auth step.
    markPendingPostAuthStep();

    try {
      const embeddKey = getEmbeddedKey();

      const result = await verifyMagicAuthCode.mutateAsync({
        email,
        code,
        embeddKey: embeddKey || undefined,
        companyId,
      });

      // VRG-490 §2 — checked BEFORE `isNewUser`: this mutation resolves
      // (doesn't throw) on a challengeRequired response too, since the
      // primary factor genuinely checked out. Without this branch the code
      // below would fall through to `navigate('/certificates')` with no
      // token ever stored.
      if (result.challengeRequired && result.ticket && result.factorType) {
        clearPendingPostAuthStep();
        suppressAutoNavRef.current = false;
        setChallenge({ ticket: result.ticket, factorType: result.factorType });
      } else if (result.isNewUser) {
        // New user → offer the optional "set a password" step.
        setShowSetPassword(true);
      } else {
        clearPendingPostAuthStep();
        suppressAutoNavRef.current = false;
        navigate('/certificates');
      }
    } catch (err: any) {
      clearPendingPostAuthStep();
      suppressAutoNavRef.current = false;
      // Prefer a clean, user-facing message. The backend surfaces WorkOS-internal
      // wording ("Failed to authenticate with Magic Auth: Invalid one-time code")
      // which should not leak to users; only fall back to a backend message when
      // it is not the generic invalid-code case.
      // Only claims the code is bad when the SERVER said so. Both branches of the
      // ternary this replaces ended at the invalid-code message, so a server-side
      // failure after a SUCCESSFUL code check was reported as a bad code — which is
      // exactly what happened when owner provisioning was throwing.
      setError(otpVerifyErrorMessage(err));
      setCode('');
    } finally {
      verifyInFlightRef.current = false;
      setIsVerifyingCode(false);
    }
  };

  const handlePasswordLogin = async () => {
    if (passwordLoginInFlightRef.current) return;

    if (!email) {
      setError('Please enter your email.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    passwordLoginInFlightRef.current = true;
    setIsPasswordLoggingIn(true);
    setError(null);
    try {
      const embeddKey = getEmbeddedKey();

      const result = await loginWithPassword.mutateAsync({
        email,
        password,
        companyId,
        embeddKey: embeddKey || undefined,
      });

      // VRG-490 §2 — see the identical branch in handleVerifyCode above:
      // this mutation resolves on a challengeRequired response too.
      if (result.challengeRequired && result.ticket && result.factorType) {
        setChallenge({ ticket: result.ticket, factorType: result.factorType });
        return;
      }

      navigate('/certificates');
    } catch (err: any) {
      // A 401 (bad credentials / org auth-method mismatch) surfaces WorkOS-internal
      // wording; show a clean message instead. Keep genuine business errors.
      const backendMsg: string | undefined = err?.response?.data?.message;
      // VERA-842: do NOT claim "incorrect password" for every 401. WorkOS also
      // returns 401 for "The user must choose an organization to finish their
      // authentication" — a multi-org account, NOT a wrong credential. Telling
      // the user their password is wrong when it is correct sends them to reset
      // a password that works. Surface non-credential 401s honestly instead.
      const isOrgSelection = /choose an organization|organization to finish/i.test(backendMsg || '');
      const isAuthFailure =
        !isOrgSelection &&
        (err?.response?.status === 401 ||
          /authentication_methods|password|credentials/i.test(backendMsg || ''));
      setError(
        isOrgSelection
          ? 'This account is linked to more than one organization, so password sign in cannot continue. Use "Send login code" to sign in.'
          : isAuthFailure
          ? 'Incorrect email or password. Please try again.'
          : backendMsg || 'Incorrect email or password. Please try again.'
      );
    } finally {
      passwordLoginInFlightRef.current = false;
      setIsPasswordLoggingIn(false);
    }
  };

  const handleSetPassword = async () => {
    if (setPasswordInFlightRef.current) return;

    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setPasswordInFlightRef.current = true;
    setIsSettingPassword(true);
    setError(null);
    try {
      await setupPassword.mutateAsync(newPassword);
      clearPendingPostAuthStep();
      suppressAutoNavRef.current = false;
      navigate('/certificates');
    } catch (err: any) {
      // VERA-837: the backend now returns 400 with WorkOS's real validation
      // message (e.g. "Password does not meet strength requirements.")
      // instead of a generic 500 — show it verbatim, with an actionable hint
      // when it's the strength-policy case. Only fall back to the generic
      // message for genuine non-validation errors (no message at all).
      const backendMsg: string | undefined = err?.response?.data?.message;
      if (backendMsg) {
        const isStrengthError = /strength requirement/i.test(backendMsg);
        setError(isStrengthError ? `${backendMsg} Try a longer, less common phrase.` : backendMsg);
      } else {
        setError('Failed to set password. You can try again later from your profile.');
      }
    } finally {
      setPasswordInFlightRef.current = false;
      setIsSettingPassword(false);
    }
  };

  const handleSkipSetPassword = () => {
    clearPendingPostAuthStep();
    suppressAutoNavRef.current = false;
    navigate('/certificates');
  };

  // TODO: what is this?
  const reduxTheme = { border_radius: 10 };

  /* Vera's own sign-in, or a brand's? Decides whether the Vera-branded backdrop
     may render at all (see the JSX below). Derived synchronously from hostname +
     install snippet, so it is stable for this page load and cannot flash. */
  const veraOwnedSurface = isVeraOwnedSurface();

  useEffect(() => {
    if (isAuthenticated && !suppressAutoNavRef.current) {
      navigate(ROUTES.CERTIFICATES);
    }
  }, [isAuthenticated]);

  return (
    <>
      {/*
        The grainy-gradient ground, on VERA'S OWN sign-in only — `apps/app` is
        white-label, and Vera purple must never paint onto a brand's storefront
        or subdomain. `AuthBackdrop` self-gates on the same synchronous check,
        so this condition governs only the shell's own background.

        When it IS shown, `bg-background` must come off: the backdrop paints its
        own base at z-index 0, and an opaque shell above it would hide the
        gradient completely. When it is NOT shown, `bg-background` stays and
        this renders exactly what it rendered before the backdrop existed.
        `relative z-[1]` is harmless in both cases and keeps content above the
        fixed backdrop layer.
      */}
      {veraOwnedSurface && <AuthBackdrop />}
      {reduxTheme && (
        <div className={`relative z-[1] flex flex-col items-center pt-[20vh] pb-8 px-4 min-h-screen w-full${veraOwnedSurface ? '' : ' bg-background'}`}>
          <div className='w-full max-w-[400px]'>
            {/*
              VERA-842: on the set-password step the page title carries the
              step, so the old "Set a new password" sub-heading underneath was
              redundant with both this and the "Set password" button.

              VRG-490: suppressed entirely while `challenge` is active —
              `TwoFactorChallenge` renders its own "Verify it's you" title,
              so this would otherwise show "Log in" directly above it.
            */}
            {!challenge && (
              <h2 className='mb-4 text-[24px] leading-[32px] font-[400]'>
                {showSetPassword ? 'Set a password' : 'Log in'}
              </h2>
            )}
            <AuthErrorMessage message={authError} sx={{ mb: 2 }} />
            <Stack sx={{ width: '100%', maxWidth: '400px' }} gap={2}>
              {challenge ? (
                <TwoFactorChallenge
                  ticket={challenge.ticket}
                  factorType={challenge.factorType}
                  onBack={() => {
                    setChallenge(null);
                    setCodeSent(false);
                    setCode('');
                    setPassword('');
                    setError(null);
                  }}
                  onVerified={() => {
                    setChallenge(null);
                    navigate('/certificates');
                  }}
                />
              ) : showSetPassword ? (
                <div>
                  <Typography variant='body3' color='text.secondary' sx={{ mb: 2, lineHeight: 1.5 }}>
                    You are already signed in. This step is optional, you can always set a password later from your profile.
                  </Typography>
                  {/*
                    VERA-837: use the shared app Input (same tint, radius 12 and
                    8px text inset) so this matches the sign-in email/password
                    fields. It was a one-off MUI TextField with borderRadius: 3.
                  */}
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder='New password'
                    value={newPassword}
                    // VERA-837: clear a stale submit error as soon as the
                    // user edits the password (see ForgotPassword for the
                    // full rationale — otherwise a failed attempt's
                    // AuthErrorMessage stays on screen and every subsequent
                    // attempt looks like it also failed).
                    onChange={value => {
                      setNewPassword(value)
                      if (error) setError(null)
                    }}
                    sx={{ '& input': { paddingLeft: '8px' } }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!setPasswordInFlightRef.current) handleSetPassword();
                      }
                    }}
                    endAdornment={
                      <InputAdornment position='end' sx={{ pr: 1 }}>
                        <IconButton
                          aria-label={showNewPassword ? 'hide the password' : 'display the password'}
                          onClick={() => setShowNewPassword(s => !s)}
                          edge='end'
                        >
                          {showNewPassword ? (
                            <EyeOffIcon className='w-5 h-5' strokeWidth={1} />
                          ) : (
                            <EyeIcon className='w-5 h-5' strokeWidth={1} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {/*
                    VERA-837: password policy is zxcvbn-style strength scoring
                    (not a rules checklist) — show live client-side feedback so
                    the user isn't guessing, mirroring WorkOS's server-side check.
                  */}
                  <PasswordStrengthMeter password={newPassword} />
                  <AuthErrorMessage message={error} />
                  <Stack spacing={2}>
                    <Button
                      variant='contained'
                      fullWidth
                      onClick={handleSetPassword}
                      // VERA-837: never disabled because the field is empty —
                      // clicking surfaces "Password must be at least 8
                      // characters." via `handleSetPassword`'s own guard
                      // instead of a dead button. In-flight is the only
                      // legitimate disable reason here.
                      disabled={isSettingPassword}
                      sx={{ paddingX: 2 }}
                    >
                      {isSettingPassword ? 'Setting password...' : 'Set password'}
                    </Button>
                    <Button
                      variant='text'
                      fullWidth
                      onClick={handleSkipSetPassword}
                      disabled={isSettingPassword}
                      sx={{ paddingX: 2 }}
                    >
                      Skip for now
                    </Button>
                  </Stack>
                </div>
              ) : codeSent ? (
                <div>
                  <Typography variant='body2' mb={2}>
                    Enter the 6-digit code sent to {email}
                  </Typography>
                  <div className='mb-4'>
                    <div className='flex flex-col'>
                      <Typography className='mb-2'>Verification Code</Typography>
                      <InputOTP
                        onChange={(value: string) => {
                          const cleanedValue = value.replace(/\D/g, '').slice(0, 6);
                          setCode(cleanedValue);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (!verifyInFlightRef.current) handleVerifyCode();
                          }
                        }}
                        value={code}
                        maxLength={6}
                        id='otp'
                        required
                        containerClassName='w-full'
                      >
                        <InputOTPGroup className='flex w-full gap-2 *:data-[slot=input-otp-slot]:rounded-md *:data-[slot=input-otp-slot]:border'>
                          <InputOTPSlot className='rounded-md border flex-1 h-12' index={0} />
                          <InputOTPSlot className='rounded-md border flex-1 h-12' index={1} />
                          <InputOTPSlot className='rounded-md border flex-1 h-12' index={2} />
                          <InputOTPSlot className='rounded-md border flex-1 h-12' index={3} />
                          <InputOTPSlot className='rounded-md border flex-1 h-12' index={4} />
                          <InputOTPSlot className='rounded-md border flex-1 h-12' index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                  </div>
                  <CodeExpiryNotice
                    issuedAt={codeIssuedAt}
                    ttlSeconds={OTP_CODE_TTL_SECONDS}
                    resendLabel='Use Resend Code below'
                  />
                  <AuthErrorMessage message={error} />
                  <Stack spacing={2}>
                    <Button
                      /*
                       * VRG-720: on expiry, Resend takes the fill and Verify
                       * steps back to outlined. Verify stays clickable — the
                       * countdown predicts the server's answer from this
                       * device's clock rather than being it, so a disable here
                       * could refuse a code the API would still accept. See the
                       * fuller note in OnboardingAuthPanel.
                       */
                      variant={codeExpired ? 'outlined' : 'contained'}
                      fullWidth
                      onClick={handleVerifyCode}
                      // VERA-837: never disabled — clicking with an incomplete
                      // code surfaces the inline validation message instead of
                      // a dead, unexplained button. Double-submit is already
                      // guarded by verifyInFlightRef inside handleVerifyCode.
                      sx={{ paddingX: 2 }}
                    >
                      {isVerifyingCode ? 'Verifying...' : 'Verify Code'}
                    </Button>
                    <Button
                      variant={codeExpired ? 'contained' : 'outlined'}
                      fullWidth
                      onClick={handleSendCode}
                      disabled={isSendingCode || isVerifyingCode}
                      sx={{ paddingX: 2 }}
                    >
                      {isSendingCode ? 'Sending...' : 'Resend Code'}
                    </Button>
                    <Button
                      variant='text'
                      fullWidth
                      onClick={() => {
                        setCodeSent(false);
                        setCode('');
                        setError(null);
                      }}
                      disabled={isVerifyingCode}
                      sx={{ paddingX: 2 }}
                    >
                      Return to login
                    </Button>
                  </Stack>
                </div>
              ) : (
                <Box>
                  <Input
                    type='email'
                    value={email}
                    onChange={setEmail}
                    placeholder='Email'
                    sx={{ mb: 2, '& input': { paddingLeft: '8px' } }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!passwordLoginInFlightRef.current) handlePasswordLogin();
                      }
                    }}
                  />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={setPassword}
                    placeholder='Password'
                    sx={{ '& input': { paddingLeft: '8px' } }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!passwordLoginInFlightRef.current) handlePasswordLogin();
                      }
                    }}
                    endAdornment={
                      <InputAdornment position='end' sx={{ pr: 1 }}>
                        <IconButton
                          aria-label={showPassword ? 'hide the password' : 'display the password'}
                          onClick={() => setShowPassword(s => !s)}
                          edge='end'
                        >
                          {showPassword ? (
                            <EyeOffIcon className='w-5 h-5' strokeWidth={1} />
                          ) : (
                            <EyeIcon className='w-5 h-5' strokeWidth={1} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  <Box sx={{ mt: 1, mb: 2, textAlign: 'right' }}>
                    {/*
                      VERA-837: "Forgot password?" now navigates to its own
                      dedicated page (was previously handled inline here,
                      which felt like nothing happened on click). Carry
                      whatever the user typed so it can prefill there.
                    */}
                    <Typography
                      component='span'
                      role='button'
                      tabIndex={0}
                      variant='body2'
                      onClick={() => navigate(ROUTES.FORGOT_PASSWORD, { state: { email } })}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ')
                          navigate(ROUTES.FORGOT_PASSWORD, { state: { email } });
                      }}
                      sx={{
                        color: 'text.secondary',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover, &:focus-visible': { color: 'text.primary' },
                      }}
                    >
                      Forgot password?
                    </Typography>
                  </Box>
                  <AuthErrorMessage message={error} />
                  <Stack spacing={2}>
                    <Button
                      variant='contained'
                      fullWidth
                      onClick={handlePasswordLogin}
                      // VERA-837: never disabled — clicking with a missing
                      // email/password surfaces the inline validation message
                      // instead of a dead button. Double-submit is guarded by
                      // passwordLoginInFlightRef inside handlePasswordLogin.
                      sx={{ paddingX: 2 }}
                    >
                      {isPasswordLoggingIn ? 'Signing in...' : 'Sign in'}
                    </Button>
                    <Button
                      variant='outlined'
                      fullWidth
                      onClick={handleSendCode}
                      // VERA-837: never disabled because the field is empty —
                      // clicking surfaces "Enter a valid email address." via
                      // `handleSendCode`'s own guard instead of a dead
                      // button. In-flight is the only legitimate disable
                      // reason here.
                      disabled={isSendingCode}
                      sx={{ paddingX: 2 }}
                    >
                      {isSendingCode ? 'Sending...' : 'Send login code'}
                    </Button>
                  </Stack>

                  <Divider sx={{ my: 2 }}>
                    {/* `text.secondary`, not Tailwind's `text-gray-500`. That
                        class is #6B7280 — a fourth "muted text" colour that never
                        went through the palette, so the pass that darkened
                        `text.secondary` for contrast would have left this one
                        label behind. It sits over the centre blob, the darkest
                        part of the auth gradient, i.e. the worst place to miss. */}
                    <Box component='span' sx={{ color: 'text.secondary' }}>
                      or
                    </Box>
                  </Divider>

                  <SocialLoginButtons />

                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant='body2' color='text.secondary'>
                      Don't have an account?{' '}
                      <Typography
                        component='span'
                        variant='body2'
                        // VERA-837: the install sign-in's "Create one" is the
                        // Owner self-serve create-account flow, NOT the
                        // VERA-1095 public self-serve-COMPANY /signup path
                        // (no company-creation/plan/Stripe on the install —
                        // this app is embedded at an already-onboarded
                        // company's install; Owners just need a Vera account).
                        onClick={() => navigate(ROUTES.CREATE_ACCOUNT)}
                        sx={{
                          color: 'text.primary',
                          fontWeight: 500,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        Create one
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          </div>
        </div>
      )}
    </>
  );
};
